import { useState, useCallback, useRef } from 'react'
import { Message, ThinkingStep } from '../types'
import * as chatApi from '../services/chatApi'

const TOOL_LABELS: Record<string, string> = {
  hubspot_search_contacts: 'Searching contacts',
  hubspot_get_contact: 'Looking up contact',
  hubspot_create_contact: 'Creating contact',
  hubspot_update_contact: 'Updating contact',
  hubspot_log_note: 'Logging note to CRM',
  hubspot_log_email: 'Logging email to CRM',
  hubspot_log_call: 'Logging call to CRM',
  hubspot_get_company: 'Looking up company',
  hubspot_create_company: 'Creating company',
  hubspot_associate_contact_to_company: 'Linking contact to company',
  hubspot_create_deal: 'Creating deal',
  hubspot_update_deal: 'Updating deal',
  hubspot_get_owners: 'Loading team members',
  gmail_search_emails: 'Searching emails',
  gmail_get_email: 'Reading email',
  gmail_send_email: 'Sending email',
  gmail_reply_to_email: 'Sending reply',
  ask_user_choices: 'Preparing options',
}

function getToolLabel(name: string): string {
  return TOOL_LABELS[name] ?? name.replace(/_/g, ' ')
}

// 'tool_start' is the only event carrying what's actually about to execute on the
// approved/edited-resume path (the 'agent' node's 'tool_call' event only ever
// reflects the model's original pre-approval arguments) — so this must update an
// existing step's args in place rather than push a duplicate, or an edited call
// would show two rows for the same tool_call id.
function upsertToolStep(steps: ThinkingStep[], event: { id: string; name: string; arguments?: string }): ThinkingStep[] {
  const existing = steps.find((s) => s.id === event.id)
  if (existing) {
    return steps.map((s) =>
      s.id === event.id ? { ...s, args: event.arguments ?? s.args, status: 'running' as const } : s
    )
  }
  return [
    ...steps,
    {
      id: event.id,
      toolName: event.name,
      label: getToolLabel(event.name),
      args: event.arguments ?? '',
      status: 'running',
    },
  ]
}

// Messages posted to seed a conversation with hidden context (e.g. "Start chat"
// from a dashboard task) are prefixed with this and kept out of the visible
// transcript — the runtime still sees them, so Zeph can open the conversation.
export const HIDDEN_BRIEF_PREFIX = '[[zeph:brief]]'

export function useChat() {
  const [allMessages, setAllMessages] = useState<Record<string, Message[]>>({})
  const [typingConvId, setTypingConvId] = useState<string | null>(null)
  const [loadingConvId, setLoadingConvId] = useState<string | null>(null)
  const [currentStatus, setCurrentStatus] = useState<string | null>(null)
  const [pendingSteps, setPendingSteps] = useState<ThinkingStep[]>([])
  const [usageWarning, setUsageWarning] = useState<{ percentUsed: number; remaining: number } | null>(null)
  const [profileIncompleteWarning, setProfileIncompleteWarning] = useState(false)
  const [pendingApproval, setPendingApproval] = useState<{
    conversationId: string
    tools: chatApi.PendingApproval[]
  } | null>(null)
  const [retryableConvs, setRetryableConvs] = useState<Record<string, boolean>>({})

  const abortControllersRef = useRef<Map<string, AbortController>>(new Map())
  // Ref for stale-closure-safe access to accumulating steps during stream
  const pendingStepsRef = useRef<ThinkingStep[]>([])
  // Ref for stale-closure-safe title generation callback
  const onTitleGeneratedRef = useRef<((title: string) => void) | undefined>()

  const loadMessages = useCallback(async (convId: string) => {
    setLoadingConvId(convId)
    try {
      const records = await chatApi.listMessages(convId)
      const messages: Message[] = records
        .filter(r => (r.role === 'user' || r.role === 'assistant') && !r.content.startsWith(HIDDEN_BRIEF_PREFIX))
        .map(r => ({
          id: r.id,
          role: r.role as 'user' | 'assistant',
          content: r.content,
          timestamp: new Date(r.createdAt),
          thinking: r.toolCalls && r.toolCalls.length > 0
            ? r.toolCalls.map((tc: any) => {
                const hasError = tc.output && typeof tc.output === 'object' && 'error' in tc.output
                return {
                  id: tc.id,
                  toolName: tc.name,
                  label: getToolLabel(tc.name),
                  args: tc.arguments,
                  result: tc.output,
                  status: hasError ? 'error' as const : 'done' as const,
                }
              })
            : undefined,
        }))
      setAllMessages(prev => ({ ...prev, [convId]: messages }))

      // Determine if the conversation is retryable based on the loaded history
      const lastMsg = messages[messages.length - 1]
      let isRetryable = false
      if (lastMsg) {
        if (lastMsg.role === 'user') {
          isRetryable = true
        } else if (lastMsg.role === 'assistant') {
          const content = lastMsg.content
          const isFailed = content.includes('[Stream failed:') || 
                          content.startsWith('Error:') || 
                          content.includes('[Stopped by user]') || 
                          content === 'Stopped by user'
          if (isFailed) {
            isRetryable = true
          }
        }
      }
      setRetryableConvs(prev => ({ ...prev, [convId]: isRetryable }))
    } catch {
      console.error('Failed to load messages for conversation', convId)
      setAllMessages(prev => ({ ...prev, [convId]: [] }))
      setRetryableConvs(prev => ({ ...prev, [convId]: false }))
    } finally {
      setLoadingConvId(null)
    }
  }, [])

  const runStreamReader = useCallback((convId: string, abort: AbortController) => {
    setTypingConvId(convId)

    // Reset thinking state for this new stream
    pendingStepsRef.current = []
    setPendingSteps([])
    setCurrentStatus(null)

    let aiMsgId: string | null = null
    let firstToken = true

    chatApi.streamConversation(
      convId,
      (event) => {
        switch (event.type) {
          case 'message_start': {
            aiMsgId = `ai-${Date.now()}`
            firstToken = true
            pendingStepsRef.current = []
            setPendingSteps([])
            setCurrentStatus(null)
            break
          }

          case 'status': {
            setCurrentStatus(event.status)
            break
          }

          case 'tool_call': {
            pendingStepsRef.current = upsertToolStep(pendingStepsRef.current, event)
            setPendingSteps([...pendingStepsRef.current])
            break
          }

          case 'tool_start': {
            pendingStepsRef.current = upsertToolStep(pendingStepsRef.current, event)
            setPendingSteps([...pendingStepsRef.current])
            break
          }

          case 'tool_result': {
            pendingStepsRef.current = pendingStepsRef.current.map(s =>
              s.id === event.toolCallId
                ? { ...s, status: 'done' as const, result: event.output }
                : s
            )
            setPendingSteps([...pendingStepsRef.current])
            break
          }

          case 'token': {
            const id = aiMsgId
            if (!id) break

            if (firstToken) {
              firstToken = false
              // Capture and clear pending steps — attach to the new message
              const stepsSnapshot = [...pendingStepsRef.current]
              pendingStepsRef.current = []
              setTypingConvId(null)
              setCurrentStatus(null)
              setPendingSteps([])
              setAllMessages(prev => ({
                ...prev,
                [convId]: [
                  ...(prev[convId] ?? []),
                  {
                    id,
                    role: 'assistant',
                    content: event.content,
                    timestamp: new Date(),
                    isStreaming: true,
                    thinking: stepsSnapshot.length > 0 ? stepsSnapshot : undefined,
                  },
                ],
              }))
            } else {
              setAllMessages(prev => ({
                ...prev,
                [convId]: (prev[convId] ?? []).map(m =>
                  m.id === id ? { ...m, content: m.content + event.content } : m
                ),
              }))
            }
            break
          }

          case 'message_complete': {
            const id = aiMsgId
            if (!id) break
            setAllMessages(prev => ({
              ...prev,
              [convId]: (prev[convId] ?? []).map(m =>
                m.id === id ? { ...m, id: event.messageId, isStreaming: false } : m
              ),
            }))
            aiMsgId = null
            setTypingConvId(null)
            setCurrentStatus(null)
            setPendingSteps([])
            pendingStepsRef.current = []
            abortControllersRef.current.delete(convId)
            setRetryableConvs(prev => ({ ...prev, [convId]: false }))
            break
          }

          case 'conversation_updated': {
            onTitleGeneratedRef.current?.(event.title)
            break
          }

          case 'usage_warning': {
            setUsageWarning({ percentUsed: event.percentUsed, remaining: event.remaining })
            break
          }

          case 'usage_blocked': {
            break
          }

          case 'profile_incomplete_warning': {
            setProfileIncompleteWarning(true)
            break
          }

          case 'approval_required': {
            setPendingApproval({
              conversationId: convId,
              tools: event.tools,
            })
            setTypingConvId(null)
            setCurrentStatus(null)
            if (aiMsgId) {
              const id = aiMsgId
              setAllMessages(prev => ({
                ...prev,
                [convId]: (prev[convId] ?? []).map(m =>
                  m.id === id ? { ...m, isStreaming: false } : m
                ),
              }))
              aiMsgId = null
            }
            break
          }

          case 'error': {
            console.error('Stream error:', event.message)
            setTypingConvId(null)
            setCurrentStatus(null)
            setPendingSteps([])
            pendingStepsRef.current = []

            setAllMessages(prev => {
              const currentMsgs = prev[convId] ?? []
              if (aiMsgId) {
                const id = aiMsgId
                aiMsgId = null
                return {
                  ...prev,
                  [convId]: currentMsgs.map(m =>
                    m.id === id ? { ...m, content: `${m.content}\n\n[Stream failed: ${event.message}]`, isStreaming: false } : m
                  ),
                }
              } else {
                const id = `error-${Date.now()}`
                return {
                  ...prev,
                  [convId]: [
                    ...currentMsgs,
                    {
                      id,
                      role: 'assistant',
                      content: `Error: ${event.message}`,
                      timestamp: new Date(),
                      isStreaming: false,
                    }
                  ]
                }
              }
            })
            setRetryableConvs(prev => ({ ...prev, [convId]: true }))
            abortControllersRef.current.delete(convId)
            break
          }
        }
      },
      abort.signal,
    ).catch(err => {
      if (abort.signal.aborted) return
      console.error('Stream failed:', err)
      setTypingConvId(null)
      setCurrentStatus(null)
      setPendingSteps([])
      pendingStepsRef.current = []

      const errMsg = err.message || String(err)
      setAllMessages(prev => {
        const currentMsgs = prev[convId] ?? []
        if (aiMsgId) {
          const id = aiMsgId
          aiMsgId = null
          return {
            ...prev,
            [convId]: currentMsgs.map(m =>
              m.id === id ? { ...m, content: `${m.content}\n\n[Stream failed: ${errMsg}]`, isStreaming: false } : m
            ),
          }
        } else {
          const id = `error-${Date.now()}`
          return {
            ...prev,
            [convId]: [
              ...currentMsgs,
              {
                id,
                role: 'assistant',
                content: `Error: ${errMsg}`,
                timestamp: new Date(),
                isStreaming: false,
              }
            ]
          }
        }
      })
      setRetryableConvs(prev => ({ ...prev, [convId]: true }))
      abortControllersRef.current.delete(convId)
    })
  }, [])

  const sendMessage = useCallback(async (
    convId: string,
    content: string,
    options?: { onTitleGenerated?: (title: string) => void; hidden?: boolean },
  ) => {
    if (!content.trim()) return

    // `hidden` messages (conversation seeds) are persisted so the runtime has
    // context, but never shown — Zeph's reply is the first visible message.
    const hidden = options?.hidden === true
    const tempId = `temp-${Date.now()}`

    if (!hidden) {
      const optimisticMsg: Message = {
        id: tempId,
        role: 'user',
        content: content.trim(),
        timestamp: new Date(),
      }
      setAllMessages(prev => ({
        ...prev,
        [convId]: [...(prev[convId] ?? []), optimisticMsg],
      }))
    }

    try {
      const saved = await chatApi.createMessage(convId, content.trim())
      if (!hidden) {
        setAllMessages(prev => ({
          ...prev,
          [convId]: (prev[convId] ?? []).map(m => m.id === tempId ? { ...m, id: saved.id } : m),
        }))
      } else {
        // Mark the conversation as "loaded" (empty) so ChatPage doesn't fire a
        // loadMessages() that could race the stream about to start below.
        setAllMessages(prev => (prev[convId] ? prev : { ...prev, [convId]: [] }))
      }
    } catch (err) {
      if (!hidden) {
        setAllMessages(prev => ({
          ...prev,
          [convId]: (prev[convId] ?? []).filter(m => m.id !== tempId),
        }))
      }
      throw err
    }

    const abort = new AbortController()
    abortControllersRef.current.set(convId, abort)
    onTitleGeneratedRef.current = options?.onTitleGenerated
    setRetryableConvs(prev => ({ ...prev, [convId]: false }))

    runStreamReader(convId, abort)
  }, [runStreamReader])

  const retry = useCallback(async (convId: string) => {
    const msgs = allMessages[convId] ?? []
    const lastUserMsgIndex = [...msgs].reverse().findIndex(m => m.role === 'user')
    if (lastUserMsgIndex === -1) return

    const actualIndex = msgs.length - 1 - lastUserMsgIndex

    // Slice messages to remove anything after the user's last message
    const cleanMessages = msgs.slice(0, actualIndex + 1)
    setAllMessages(prev => ({
      ...prev,
      [convId]: cleanMessages,
    }))

    setRetryableConvs(prev => ({ ...prev, [convId]: false }))

    const abort = new AbortController()
    abortControllersRef.current.set(convId, abort)

    runStreamReader(convId, abort)
  }, [allMessages, runStreamReader])

  // Stop streaming without clearing messages
  const stopStream = useCallback((convId: string) => {
    const ctrl = abortControllersRef.current.get(convId)
    ctrl?.abort()
    abortControllersRef.current.delete(convId)
    setTypingConvId(prev => (prev === convId ? null : prev))
    setCurrentStatus(null)
    setPendingSteps([])
    pendingStepsRef.current = []
    setAllMessages(prev => ({
      ...prev,
      [convId]: (prev[convId] ?? []).map(m =>
        m.isStreaming ? { ...m, isStreaming: false } : m
      ),
    }))
    setRetryableConvs(prev => ({ ...prev, [convId]: true }))
  }, [])

  const dismissUsageWarning = useCallback(() => {
    setUsageWarning(null)
  }, [])

  const dismissProfileIncompleteWarning = useCallback(() => {
    setProfileIncompleteWarning(false)
  }, [])

  // Chat only learns about usage from SSE events emitted at the end of a
  // completed turn, so the banner otherwise stays stale if the owner changes
  // an allocation mid-session. Call this to pull the live status instead —
  // e.g. on conversation load and when the tab regains focus.
  const refreshUsageStatus = useCallback(async (workspaceId: string) => {
    try {
      const status = await chatApi.getMyCreditStatus(workspaceId)
      if (status.percentUsed !== null && status.percentUsed >= 90 && status.remaining !== null) {
        setUsageWarning({ percentUsed: status.percentUsed, remaining: status.remaining })
      } else {
        setUsageWarning(null)
      }
    } catch {
      // non-fatal — leave whatever warning state was already shown
    }
  }, [])

  const clearMessages = useCallback((convId: string) => {
    const ctrl = abortControllersRef.current.get(convId)
    ctrl?.abort()
    abortControllersRef.current.delete(convId)
    setAllMessages(prev => ({ ...prev, [convId]: [] }))
    setTypingConvId(prev => (prev === convId ? null : prev))
    setCurrentStatus(null)
    setPendingSteps([])
    pendingStepsRef.current = []
    setPendingApproval(null)
    setRetryableConvs(prev => ({ ...prev, [convId]: false }))
  }, [])

  const handleApprovalDecision = useCallback(async (
    convId: string,
    decision: { action: 'approved' | 'rejected' | 'edited'; editedToolCalls?: any[] }
  ) => {
    setPendingApproval(null)
    setRetryableConvs(prev => ({ ...prev, [convId]: false }))

    const abort = new AbortController()
    abortControllersRef.current.set(convId, abort)
    setTypingConvId(convId)

    let aiMsgId: string | null = null
    let firstToken = true

    try {
      await chatApi.submitApproval(
        convId,
        decision,
        (event) => {
          switch (event.type) {
            case 'message_start': {
              aiMsgId = `ai-${Date.now()}`
              firstToken = true
              break
            }
            case 'status': {
              setCurrentStatus(event.status)
              break
            }
            case 'tool_call': {
              pendingStepsRef.current = upsertToolStep(pendingStepsRef.current, event)
              setPendingSteps([...pendingStepsRef.current])
              break
            }
            case 'tool_start': {
              pendingStepsRef.current = upsertToolStep(pendingStepsRef.current, event)
              setPendingSteps([...pendingStepsRef.current])
              break
            }
            case 'tool_result': {
              pendingStepsRef.current = pendingStepsRef.current.map(s =>
                s.id === event.toolCallId
                  ? { ...s, status: 'done' as const, result: event.output }
                  : s
              )
              setPendingSteps([...pendingStepsRef.current])
              break
            }
            case 'token': {
              const id = aiMsgId
              if (!id) break

              if (firstToken) {
                firstToken = false
                const stepsSnapshot = [...pendingStepsRef.current]
                pendingStepsRef.current = []
                setTypingConvId(null)
                setCurrentStatus(null)
                setPendingSteps([])
                setAllMessages(prev => ({
                  ...prev,
                  [convId]: [
                    ...(prev[convId] ?? []),
                    {
                      id,
                      role: 'assistant',
                      content: event.content,
                      timestamp: new Date(),
                      isStreaming: true,
                      thinking: stepsSnapshot.length > 0 ? stepsSnapshot : undefined,
                    },
                  ],
                }))
              } else {
                setAllMessages(prev => ({
                  ...prev,
                  [convId]: (prev[convId] ?? []).map(m =>
                    m.id === id ? { ...m, content: m.content + event.content } : m
                  ),
                }))
              }
              break
            }
            case 'message_complete': {
              const id = aiMsgId
              if (!id) break
              setAllMessages(prev => ({
                ...prev,
                [convId]: (prev[convId] ?? []).map(m =>
                  m.id === id ? { ...m, id: event.messageId, isStreaming: false } : m
                ),
              }))
              aiMsgId = null
              setTypingConvId(null)
              setCurrentStatus(null)
              setPendingSteps([])
              pendingStepsRef.current = []
              abortControllersRef.current.delete(convId)
              setRetryableConvs(prev => ({ ...prev, [convId]: false }))
              break
            }
            case 'approval_required': {
              setPendingApproval({
                conversationId: convId,
                tools: event.tools,
              })
              setTypingConvId(null)
              setCurrentStatus(null)
              if (aiMsgId) {
                const id = aiMsgId
                setAllMessages(prev => ({
                  ...prev,
                  [convId]: (prev[convId] ?? []).map(m =>
                    m.id === id ? { ...m, isStreaming: false } : m
                  ),
                }))
                aiMsgId = null
              }
              break
            }
            case 'usage_warning': {
              setUsageWarning({ percentUsed: event.percentUsed, remaining: event.remaining })
              break
            }
            case 'usage_blocked': {
              break
            }
            case 'profile_incomplete_warning': {
              setProfileIncompleteWarning(true)
              break
            }
            case 'error': {
              console.error('Approval stream error:', event.message)
              setTypingConvId(null)
              setCurrentStatus(null)
              setPendingSteps([])
              pendingStepsRef.current = []

              setAllMessages(prev => {
                const currentMsgs = prev[convId] ?? []
                if (aiMsgId) {
                  const id = aiMsgId
                  aiMsgId = null
                  return {
                    ...prev,
                    [convId]: currentMsgs.map(m =>
                      m.id === id ? { ...m, content: `${m.content}\n\n[Stream failed: ${event.message}]`, isStreaming: false } : m
                    ),
                  }
                } else {
                  const id = `error-${Date.now()}`
                  return {
                    ...prev,
                    [convId]: [
                      ...currentMsgs,
                      {
                        id,
                        role: 'assistant',
                        content: `Error: ${event.message}`,
                        timestamp: new Date(),
                        isStreaming: false,
                      }
                    ]
                  }
                }
              })
              setRetryableConvs(prev => ({ ...prev, [convId]: true }))
              abortControllersRef.current.delete(convId)
              break
            }
          }
        },
        abort.signal
      )
    } catch (err) {
      if (abort.signal.aborted) return
      console.error('Approval stream failed:', err)
      setTypingConvId(null)
      setCurrentStatus(null)
      setPendingSteps([])
      pendingStepsRef.current = []

      const errMsg = err.message || String(err)
      setAllMessages(prev => {
        const currentMsgs = prev[convId] ?? []
        if (aiMsgId) {
          const id = aiMsgId
          aiMsgId = null
          return {
            ...prev,
            [convId]: currentMsgs.map(m =>
              m.id === id ? { ...m, content: `${m.content}\n\n[Stream failed: ${errMsg}]`, isStreaming: false } : m
            ),
          }
        } else {
          const id = `error-${Date.now()}`
          return {
            ...prev,
            [convId]: [
              ...currentMsgs,
              {
                id,
                role: 'assistant',
                content: `Error: ${errMsg}`,
                timestamp: new Date(),
                isStreaming: false,
              }
            ]
          }
        }
      })
      setRetryableConvs(prev => ({ ...prev, [convId]: true }))
      abortControllersRef.current.delete(convId)
    }
  }, [])

  return {
    allMessages,
    typingConvId,
    loadingConvId,
    currentStatus,
    pendingSteps,
    usageWarning,
    dismissUsageWarning,
    profileIncompleteWarning,
    dismissProfileIncompleteWarning,
    refreshUsageStatus,
    pendingApproval,
    sendMessage,
    loadMessages,
    stopStream,
    clearMessages,
    handleApprovalDecision,
    retry,
    retryableConvs,
  }
}
